import React from 'react';
import { Alert, Box, Typography, IconButton, Tooltip } from '@mui/material';
import { Button } from 'components/index';
import { Bookmark as BookmarkIcon, BookmarkBorder as BookmarkBorderIcon } from '@mui/icons-material';
import { MaterialReactTable, useMaterialReactTable, type MRT_ColumnDef, type MRT_Cell } from 'material-react-table';
import { SectionFieldDTO } from '../magic-report.type';
import { ProjectLineItemDTO } from '../../../project/project.type';
import { calculateTotalCompletedStored } from '../../../project/project.util';
import { formatCurrency } from '../../../../common/utils/common.util';
import { usePrompt } from '../../../../components/AppPrompt';

export interface ReductionRow {
  lineItemId: string;
  description: string;
  requestedAmount: number;
  percentRequested: number;
  percentRecommended: number;
  fundingAmount: number;
  reducedBy: number;
  revisedBudget: number;
  previousApplication: number;
  conditionType: 'no-work' | 'partial';
}

const parseRow = (value?: string): ReductionRow | null => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    return parsed as ReductionRow;
  } catch {
    return null;
  }
};

const serializeRow = (row: ReductionRow): string => JSON.stringify(row);

const ensureRowDefaults = (row: Partial<ReductionRow>): ReductionRow => {
  const requestedAmount = row.requestedAmount ?? 0;
  const fundingAmount = row.fundingAmount ?? 0;
  const revisedBudget = row.revisedBudget ?? 0;
  const previousApplication = row.previousApplication ?? 0;

  const totalRequestedToDate = previousApplication + requestedAmount;
  const totalRecommendedToDate = previousApplication + fundingAmount;

  const percentRequested = row.percentRequested ?? (revisedBudget !== 0 ? (totalRequestedToDate / revisedBudget) * 100 : 0);
  const percentRecommended = row.percentRecommended ?? (revisedBudget !== 0 ? (totalRecommendedToDate / revisedBudget) * 100 : 0);

  return {
    lineItemId: row.lineItemId || '',
    description: row.description || '',
    requestedAmount,
    percentRequested,
    percentRecommended,
    fundingAmount,
    reducedBy: row.reducedBy ?? requestedAmount - fundingAmount,
    revisedBudget,
    previousApplication,
    conditionType: row.conditionType || 'partial',
  };
};

interface ReductionCommentsTableProps {
  rowsField: SectionFieldDTO | undefined;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
  lineItems: ProjectLineItemDTO[] | undefined;
  onRowsSynced?: (rows: ReductionRow[]) => void;
  /** Whether an empty table may fill itself from the budget (first open of a period only). */
  autoSync?: boolean;
}

// Rounds a recommended percent to the nearest whole percent, except when it would round up to
// 100% while the requested percent also rounds to 100% — in that case round down to 99% so the
// recommendation still visibly reads as a reduction.
export const roundRecommendedPercent = (percentRecommended: number, percentRequested: number): number => {
  const rounded = Math.round(percentRecommended);
  if (rounded >= 100 && percentRecommended < 100 && Math.round(percentRequested) >= 100) {
    return 99;
  }
  return rounded;
};

const formatPercent = (value: number): string => `${Math.round(value)}%`;
const formatRecommendedPercent = (percentRecommended: number, percentRequested: number): string =>
  `${roundRecommendedPercent(percentRecommended, percentRequested)}%`;

export const ReductionCommentsTable: React.FC<ReductionCommentsTableProps> = ({
  rowsField,
  handleFieldChange,
  lineItems,
  onRowsSynced,
  autoSync = false,
}) => {
  const { getConfirmation } = usePrompt();

  const currentRows: ReductionRow[] = rowsField?.entries?.map(e => parseRow(e.value)).filter((r): r is ReductionRow => r !== null) || [];

  const buildRowsFromLineItems = (): ReductionRow[] => {
    const items = (lineItems || []).filter(li => !li.isDeleted);

    if (!items.length) return [];

    const rows: ReductionRow[] = [];

    items.forEach(li => {
      const previousApplication = li.previousApplication || 0;
      const requestedIncrement = Math.round(((li.completeThisPeriod || 0) + (li.storedThisPeriod || 0)) * 100) / 100;
      const cdccApproved = Math.round((li.cdccApproved || 0) * 100) / 100;
      const completedStoredToDate = Math.round(calculateTotalCompletedStored(li) * 100) / 100;
      const revisedBudget = li.revisedBudget || li.originalBudget || 0;

      if (requestedIncrement <= 0) return;

      const isReduction = cdccApproved < requestedIncrement;
      const isNoWorkCondition = isReduction && completedStoredToDate === 0;
      const isPartialCondition = isReduction && completedStoredToDate !== 0;

      if (!isNoWorkCondition && !isPartialCondition) {
        return;
      }

      const conditionType: ReductionRow['conditionType'] = isNoWorkCondition ? 'no-work' : 'partial';

      // Always rebuild from the current line item data (matches the refresh confirmation copy:
      // "Any manual edits you have made in this table will be overwritten"). Merging from the
      // previously saved row here would keep fundingAmount/percentRequested/percentRecommended/
      // reducedBy frozen at their old values even after completeThisPeriod/storedThisPeriod change.
      rows.push(
        ensureRowDefaults({
          lineItemId: li.id,
          description: li.lineItem?.description || '',
          requestedAmount: requestedIncrement,
          fundingAmount: cdccApproved,
          revisedBudget,
          previousApplication,
          conditionType,
        }),
      );
    });

    return rows;
  };

  const syncRowsFromBudget = async (skipConfirmation = false) => {
    if (!skipConfirmation) {
      const isConfirmed = await getConfirmation({
        heading: 'Get Reduction Comments',
        description:
          'This will refresh the Reduction Comments table with the latest data from the Review Budget step. Any manual edits you have made in this table will be overwritten. Are you sure you want to continue?',
        confirmButton: {
          label: 'Continue',
          color: 'primary',
        },
        cancelButton: {
          label: 'Cancel',
          color: 'secondary',
        },
      });

      if (!isConfirmed) return;
    }

    const nextRows = buildRowsFromLineItems();

    const entries =
      nextRows.length === 0
        ? []
        : nextRows.map((row, index) => ({
            order: index,
            value: serializeRow(row),
          }));

    handleFieldChange('ReductionCommentsRows', {
      entries,
    });

    onRowsSynced?.(nextRows);
  };

  const rowsToRender: ReductionRow[] = React.useMemo(() => {
    if (currentRows.length > 0) {
      return currentRows.map(r => ensureRowDefaults(r));
    }
    return autoSync ? buildRowsFromLineItems() : [];
    // Depend on rowsField (not currentRows.length) so a refresh that keeps the same row count
    // but changes values (e.g. "Get Reduction Comments" after editing completeThisPeriod)
    // is actually reflected — currentRows.length alone doesn't change in that case.
  }, [rowsField, lineItems, autoSync]);

  React.useEffect(() => {
    if (!currentRows.length && rowsToRender.length) {
      const entries = rowsToRender.map((row, index) => ({
        order: index,
        value: serializeRow(row),
      }));

      handleFieldChange('ReductionCommentsRows', { entries });
      onRowsSynced?.(rowsToRender);
    }
  }, [rowsToRender.length]);

  const handleSaveCell = React.useCallback(
    (cell: MRT_Cell<ReductionRow>, value: any) => {
      const updatedRows = rowsToRender.map(r => ({ ...r }));
      const row = cell.row.original;
      const field = cell.column.id as keyof ReductionRow;

      let updatedValue: any = value;

      if (field === 'fundingAmount') {
        const numeric = parseFloat(value);
        updatedValue = Number.isNaN(numeric) ? 0 : numeric;
      }

      const index = updatedRows.findIndex(r => r.lineItemId === row.lineItemId);
      if (index === -1) return;

      const updatedRow: ReductionRow = ensureRowDefaults({
        ...updatedRows[index],
        [field]: updatedValue,
      });

      updatedRows[index] = updatedRow;

      const entries = updatedRows.map((r, idx) => ({
        order: idx,
        value: serializeRow(r),
      }));

      handleFieldChange('ReductionCommentsRows', { entries });
      onRowsSynced?.(updatedRows);
    },
    [handleFieldChange, onRowsSynced, rowsToRender],
  );

  const rightAlignedCellProps = {
    align: 'right' as const,
    sx: {
      fontSize: '0.875rem',
      border: 'none',
    },
  };

  const rightAlignedHeadCellProps = {
    align: 'right' as const,
  };

  const columns = React.useMemo<MRT_ColumnDef<ReductionRow>[]>(
    () => [
      {
        accessorKey: 'description',
        header: 'Line Item',
        size: 250,
      },
      {
        accessorKey: 'requestedAmount',
        header: 'Requested Amount',
        size: 120,
        Cell: ({ cell }) => formatCurrency(cell.getValue<number>()?.toString() ?? '0', true),
        muiTableBodyCellProps: rightAlignedCellProps,
        muiTableHeadCellProps: rightAlignedHeadCellProps,
      },
      {
        accessorKey: 'percentRequested',
        header: '% Requested',
        size: 110,
        Cell: ({ cell }) => formatPercent(cell.getValue<number>() ?? 0),
        muiTableBodyCellProps: rightAlignedCellProps,
        muiTableHeadCellProps: rightAlignedHeadCellProps,
      },
      {
        accessorKey: 'percentRecommended',
        header: '% Recommended',
        size: 130,
        Cell: ({ row, cell }) => formatRecommendedPercent(cell.getValue<number>() ?? 0, row.original.percentRequested ?? 0),
        muiTableBodyCellProps: rightAlignedCellProps,
        muiTableHeadCellProps: rightAlignedHeadCellProps,
      },
      {
        accessorKey: 'fundingAmount',
        header: 'Funding Amount',
        size: 130,
        Cell: ({ cell }) => formatCurrency(cell.getValue<number>()?.toString() ?? '0', true),
        muiEditTextFieldProps: ({ cell }) => ({
          type: 'number',
          onBlur: event => {
            handleSaveCell(cell, event.target.value);
          },
        }),
        muiTableBodyCellProps: rightAlignedCellProps,
        muiTableHeadCellProps: rightAlignedHeadCellProps,
      },
      {
        accessorKey: 'reducedBy',
        header: 'Reduced By',
        size: 130,
        Cell: ({ cell }) => formatCurrency(cell.getValue<number>()?.toString() ?? '0', true),
        muiTableBodyCellProps: rightAlignedCellProps,
        muiTableHeadCellProps: rightAlignedHeadCellProps,
      },
    ],
    [handleSaveCell],
  );

  const table = useMaterialReactTable({
    columns,
    data: rowsToRender,
    initialState: {
      density: 'compact',
      showGlobalFilter: false,
    },
    enableColumnFilters: false,
    enableColumnActions: false,
    enableColumnResizing: true,
    enableColumnPinning: false,
    enablePagination: false,
    enableDensityToggle: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableRowSelection: false,
    enableSorting: false,
    layoutMode: 'grid',
    editDisplayMode: 'cell',
    enableEditing: true,
    muiTableContainerProps: {
      sx: {
        padding: '0px',
        height: '100%',
        maxHeight: 400,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100%',
        overflow: 'auto',
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: 'none',
        backgroundColor: 'transparent',
      },
    },
    muiTableHeadProps: {
      sx: {
        position: 'sticky',
        top: 0,
        zIndex: 2,
        padding: 0,
        color: 'white',
      },
    },
    muiTableHeadCellProps: {
      sx: (theme: any) => ({
        backgroundColor: theme.palette.primary.main + ' !important',
        color: '#ffffff',
        padding: '8px 9px',
        border: 'none',
        fontSize: '0.9rem',
        fontWeight: 'normal',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        '& .MuiBox-root': {
          whiteSpace: 'normal',
          wordWrap: 'break-word',
        },
      }),
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: row.index % 2 === 0 ? '#f9f9f9' : '#ffffff',
      },
    }),
    muiTableBodyCellProps: {
      sx: {
        border: 'none',
        fontSize: '0.875rem',
      },
    },
  });

  const budgetRows = buildRowsFromLineItems();
  const isOutOfDate =
    !autoSync &&
    (budgetRows.length !== currentRows.length ||
      budgetRows.some(budgetRow => {
        const row = currentRows.find(r => r.lineItemId === budgetRow.lineItemId);
        return (
          !row ||
          row.conditionType !== budgetRow.conditionType ||
          Math.abs((row.requestedAmount || 0) - (budgetRow.requestedAmount || 0)) > 0.005 ||
          Math.abs((row.fundingAmount || 0) - (budgetRow.fundingAmount || 0)) > 0.005
        );
      }));
  const outOfDateAlert = isOutOfDate ? (
    <Alert severity='warning' sx={{ mb: 1, fontSize: '0.875rem' }}>
      Reduction Comments are not synced with the current budget. Click &quot;Get Reduction Comments&quot; to refresh the data.
    </Alert>
  ) : null;

  if (!rowsToRender.length) {
    return (
      <Box sx={{ mt: 1, mb: 2 }}>
        {outOfDateAlert}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            No reductions were detected based on the current Review Budget data.
          </Typography>
          <Button variant='outlined' size='small' onClick={() => syncRowsFromBudget(true)}>
            Get Reduction Comments
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
      {outOfDateAlert}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant='body2' color='text.secondary' fontWeight='medium'>
          Reduction Comments
        </Typography>
        <Tooltip title='Refresh reductions from the budget' arrow>
          <Button variant='outlined' size='small' onClick={() => syncRowsFromBudget(true)}>
            Get Reduction Comments
          </Button>
        </Tooltip>
      </Box>

      {/* Bookmark + table layout (bookmark on the left, table on the right) */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, width: '100%', overflow: 'hidden' }}>
        <IconButton
          size='small'
          onClick={() => handleFieldChange('ReductionCommentsRows', { addToActionItems: !(rowsField?.addToActionItems || false) })}
          sx={{ mt: 0.5, flexShrink: 0 }}
        >
          {rowsField?.addToActionItems ? (
            <BookmarkIcon color='primary' sx={{ fontSize: 20 }} />
          ) : (
            <BookmarkBorderIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
          )}
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <MaterialReactTable table={table} />
        </Box>
      </Box>
    </Box>
  );
};
